import { useState } from "react";
import { useSpring } from "react-spring";
import { colors, screens } from "../constants/constants";
import { springConfigs } from "../constants/springConfigs";
import { useMargins } from "../hooks/hooks";
import { useWindowSize } from "../hooks/useWindowSize";
import { OnCurtainStart } from "../types/types";
import {
  Arrows,
  LeftArrow,
  RightArrow,
} from "./ScreenLanding/components/Arrows";
import { Contour } from "./ScreenLanding/components/Contour";
import { IocoText } from "./ScreenLanding/components/IocoText";
import { PageNumber } from "./ScreenLanding/components/PageNumber";
import { SVGCircle } from "./ScreenMenu/components/SVGCircle";
import { Hamburger } from "./shared/Hamburger";
import { ScreenBackground } from "./shared/ScreenBackground";
import { AnimatedMedia } from "./shared/SVGComponents";
import { Video } from "./shared/Video";

const Sliders = ({ children }) => {
  const { w, h, horizontal } = useWindowSize();

  return (
    <div
      id=""
      className=""
      {...{
        style: {
          gridArea: horizontal ? "1/5/-1/-2" : "7/2/-1/-1",
          background: "#fff1",
          display: "grid",
          zIndex: 99,
          padding: w(3),
          fontSize: horizontal ? h(3) : h(2),
        },
      }}
    >
      <div id="" className="" {...{ style: { placeSelf: "center" } }}>
        MEDIA CONTROLS
      </div>
      {children}
      <div></div>
    </div>
  );
};

const HueSlider = ({
  hue,
  hueSet,
}: {
  hue: number;
  hueSet: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { h } = useWindowSize();
  return (
    <div
      id="Slider"
      className=""
      {...{
        style: {
          display: "grid",
          gridTemplate: `
          "label value"
          "slider slider"
          `,
        },
      }}
    >
      <div
        id="label"
        className=""
        {...{ style: { gridArea: "label", placeSelf: "end center" } }}
      >
        Hue
      </div>
      <div
        id="value"
        className=""
        {...{
          style: {
            gridArea: "value",
            placeSelf: "end center",
            minWidth: "100%",
            textAlign: "center",
          },
        }}
      >
        <div id="">{hue}</div>
      </div>
      <input
        type="range"
        id="slider"
        className=""
        {...{
          style: {
            gridArea: "slider",
            placeSelf: "center",
            display: "grid",
            width: "100%",
          },
          onChange: (event) => {
            hueSet(Number(event.target.value));
            // console.log(event.target.value);
          },
          min: 0,
          max: 359,
          value: hue,
        }}
      ></input>
    </div>
  );
};

const InvertSlider = ({
  invert,
  invertSet,
}: {
  invert: number;
  invertSet: React.Dispatch<React.SetStateAction<number>>;
}) => {
  return (
    <div
      className=""
      {...{
        style: {
          display: "grid",
          gridTemplate: `
          "label value"
          "slider slider"
          `,
        },
      }}
    >
      <div
        id="label"
        className=""
        {...{
          style: {
            gridArea: "label",
            placeSelf: "end center",
            minWidth: "100%",
            textAlign: "center",
          },
        }}
      >
        Invert
      </div>
      <div
        id="value"
        className=""
        {...{ style: { gridArea: "value", placeSelf: "end center" } }}
      >
        <div id="">{invert}%</div>
      </div>
      <input
        type="range"
        className=""
        {...{
          style: {
            gridArea: "slider",
            placeSelf: "center",
            display: "grid",
            width: "100%",
          },
          onChange: (event) => {
            invertSet(Number(event.target.value));
          },
          min: 0,
          max: 100,
          value: invert,
        }}
      ></input>
    </div>
  );
};

const ContrastSlider = ({
  contrast,
  contrastSet,
}: {
  contrast: number;
  contrastSet: React.Dispatch<React.SetStateAction<number>>;
}) => {
  return (
    <div
      className=""
      {...{
        style: {
          display: "grid",
          gridTemplate: `
          "label value"
          "slider slider"
          `,
        },
      }}
    >
      <div
        id="label"
        className=""
        {...{
          style: {
            gridArea: "label",
            placeSelf: "end center",
            minWidth: "100%",
            textAlign: "center",
          },
        }}
      >
        Contrast
      </div>
      <div
        id="value"
        className=""
        {...{ style: { gridArea: "value", placeSelf: "end center" } }}
      >
        <div id="">{contrast}%</div>
      </div>
      <input
        type="range"
        className=""
        {...{
          style: {
            gridArea: "slider",
            placeSelf: "center",
            display: "grid",
            width: "100%",
          },
          onChange: (event) => {
            contrastSet(Number(event.target.value));
          },
          min: 100,
          max: 2000,
          value: contrast,
        }}
      ></input>
    </div>
  );
};

const BlurSlider = ({
  blur,
  blurSet,
}: {
  blur: number;
  blurSet: React.Dispatch<React.SetStateAction<number>>;
}) => {
  return (
    <div
      className=""
      {...{
        style: {
          display: "grid",
          gridTemplate: `
          "label value"
          "slider slider"
          `,
        },
      }}
    >
      <div
        id="label"
        className=""
        {...{
          style: {
            gridArea: "label",
            placeSelf: "end center",
            minWidth: "100%",
            textAlign: "center",
          },
        }}
      >
        Blur
      </div>
      <div
        id="value"
        className=""
        {...{ style: { gridArea: "value", placeSelf: "end center" } }}
      >
        <div id="">{blur}px</div>
      </div>
      <input
        type="range"
        className=""
        {...{
          style: {
            gridArea: "slider",
            placeSelf: "center",
            display: "grid",
            width: "100%",
          },
          onChange: (event) => {
            blurSet(Number(event.target.value));
          },
          min: 0,
          max: 60,
          value: blur,
        }}
      ></input>
    </div>
  );
};
const RotateSlider = ({
  rotate,
  rotateSet,
}: {
  rotate: number;
  rotateSet: React.Dispatch<React.SetStateAction<number>>;
}) => {
  return (
    <div
      className=""
      {...{
        style: {
          display: "grid",
          gridTemplate: `
          "label value"
          "slider slider"
          `,
        },
      }}
    >
      <div
        id="label"
        className=""
        {...{
          style: {
            gridArea: "label",
            placeSelf: "end center",
            minWidth: "100%",
            textAlign: "center",
          },
        }}
      >
        Rotate
      </div>
      <div
        id="value"
        className=""
        {...{ style: { gridArea: "value", placeSelf: "end center" } }}
      >
        <div id="">{rotate} deg</div>
      </div>
      <input
        type="range"
        className=""
        {...{
          style: {
            gridArea: "slider",
            placeSelf: "center",
            display: "grid",
            width: "100%",
          },
          onChange: (event) => {
            rotateSet(Number(event.target.value));
          },
          min: 0,
          max: 360,
          value: rotate,
        }}
      ></input>
    </div>
  );
};

export const ScreenMedia = ({
  isCurtainActive,
  isCurtainIntro,
  onCurtainStart,
}: {
  isCurtainActive: boolean;
  isCurtainIntro: boolean;
  onCurtainStart: OnCurtainStart;
}) => {
  const background = `
  radial-gradient(circle at 50% 0%, #703CAF55 0, transparent 50%),
  radial-gradient(circle at 50% 0%, #3A2255BB 0, transparent 90%),
  linear-gradient(#2A225A, #190E29)
  `;

  const isContentShown =
    !isCurtainActive || (isCurtainActive && isCurtainIntro);

  const { liftAsset } = useSpring({
    from: { liftAsset: "10%" },
    to: { liftAsset: "0%" },
    config: {
      tension: 180,
      friction: 120,
      mass: 10,
      precision: 0.00001,
    },
  });

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: springConfigs.assetAppears,
  });

  const [hue, hueSet] = useState(0);
  const [invert, invertSet] = useState(0);
  const [contrast, contrastSet] = useState(100);
  const [blur, blurSet] = useState(0);
  const [rotate, rotateSet] = useState(0);

  // console.log({ hue, invert });

  const { h, w, horizontal, vertical } = useWindowSize();
  const { screenContentVerticalPadding, screenContentHorizontalPadding } =
    useMargins();

  return (
    <>
      <ScreenBackground
        {...{
          background,
          screenName: screens.preorder,
          gridArea: "AppContentContainer",
        }}
      />
      <Contour {...{ gridArea: "AppContentContainer" }} />

      {/* <Lines {...{ gridArea: "AppContentContainer" }} /> */}

      {isContentShown && (
        <div
          id="contentGrid"
          className=""
          {...{
            style: {
              gridArea: "AppContentContainer",
              display: "grid",
              padding: `${screenContentVerticalPadding} ${screenContentHorizontalPadding}`, // start with first line
              gridTemplateColumns: `repeat(${
                horizontal ? 7 : 4
              }, minmax(0, 1fr))`, // https://stackoverflow.com/questions/43311943/prevent-content-from-expanding-grid-items
              gridTemplateRows: "repeat(11, minmax(0, 1fr))", // https://stackoverflow.com/questions/43311943/prevent-content-from-expanding-grid-items
            },
          }}
        >
          <Video
            {...{
              containerStyle: {
                gridArea: horizontal ? "1/1/-1/6" : "1/1/-1/6",
                width: horizontal ? w(60) : "125%",
              },
              videoStyle: {
                // width: "64%",
                placeSelf: "center ",
                marginTop: liftAsset,
                opacity,
                filter: `
                hue-rotate(${hue}deg) 
                invert(${invert}%) 
                contrast(${contrast}%) 
                blur(${blur}px) 
                `,
                transform: `rotate(${rotate}deg`,
              },
            }}
          />
          <IocoText
            {...{
              onCurtainStart,
              gridArea: horizontal ? "1/1" : "1/1", // second row first colum
            }}
          />
          <Hamburger
            {...{
              onCurtainStart,
              gridArea: horizontal ? "1/7/1/8" : "1/-3/1/-1",
            }}
          />
          {horizontal && (
            <Arrows
              {...{
                gridArea: "5/1/7/2",
                onCurtainStart,
                screenLeft: "performance",
                screenRight: "preorder",
              }}
            />
          )}
          {vertical && (
            <div
              id=""
              {...{
                style: {
                  gridArea: "-3/1/-1/2",
                  display: "grid",
                  // placeSelf: "center",
                  width: "66.6%",
                  gap: h(1),
                },
              }}
            >
              <LeftArrow
                {...{
                  onCurtainStart,
                  screenLeft: "performance",
                  screenRight: "preorder",
                  containerStyle: {
                    width: "87.5%", // matain a 7/8th ratio
                    placeSelf: "start",
                  },
                }}
              />
              <RightArrow
                {...{
                  onCurtainStart,
                  screenLeft: "performance",
                  screenRight: "preorder",
                }}
              />
            </div>
          )}
          <PageNumber
            {...{
              gridArea: horizontal ? "5/7/7/8" : "2/1/3/4",
              pageNumber: "04",
            }}
          />
          <div
            id="animated-svg"
            {...{
              style: {
                gridArea: horizontal ? "-2/6/-1/8" : "4/1/4/3",

                color: colors.pink,
                fontSize: h(1),
                letterSpacing: w(1),
                placeSelf: horizontal ? "end" : "start",
                zIndex: 999,
                display: "grid",
                gridTemplate: `
                    "a"
                    "b"
                  `,
                gap: h(2),
              },
            }}
          >
            <SVGCircle
              {...{
                active: true,
                appearSlideUp: false,
                style: {
                  margin: "auto",
                },
                containerStyle: {
                  // gridArea: "5/-3",
                  placeSelf: "end",
                  padding: 0,
                },
                svgComponent: (
                  <AnimatedMedia
                    {...{ active: false, slideComponentUp: false }}
                  />
                ),
              }}
            />
            <div
              id=""
              className=""
              {...{ style: { placeSelf: "end", marginRight: `-${w(1)}` } }}
            >
              IOCO MEDIA
            </div>
          </div>
          <Sliders>
            <HueSlider {...{ hue, hueSet }} />
            <InvertSlider {...{ invert, invertSet }} />
            <ContrastSlider {...{ contrast, contrastSet }} />
            <BlurSlider {...{ blur, blurSet }} />
            <RotateSlider {...{ rotate, rotateSet }} />
          </Sliders>
        </div>
      )}
    </>
  );
};
